import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Mic, Square, Loader2, Pencil, ArrowLeft } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const GuestVoiceMessage = forwardRef(function GuestVoiceMessage({ guestId, disabled, onBusyChange, onSent, embedded = false, onStateChange, onBack, onUseText }, ref) {
  const [ready, setReady] = useState(false);
  const [phase, setPhase] = useState("idle");
  const [seconds, setSeconds] = useState(0);
  const [draft, setDraft] = useState(null);
  const [englishText, setEnglishText] = useState("");
  const attemptedText = useRef(null);
  const [editing, setEditing] = useState(false);
  const holding = useRef(false);
  const draftRef = useRef(null);
  const delivered = useRef(false);
  const [error, setError] = useState("");
  const recorder = useRef(null);
  const stream = useRef(null);
  const timer = useRef(null);
  const controller = useRef(null);
  const generation = useRef(0);
  const phaseRef = useRef("idle");
  phaseRef.current = phase;
  draftRef.current = draft;
  useEffect(() => { onStateChange?.({ phase, seconds, ready, error }); }, [phase, seconds, ready, error, onStateChange]);

  const releaseMicrophone = () => {
    clearInterval(timer.current);
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
  };
  const discard = () => {
    holding.current = false;
    if (draftRef.current && !delivered.current) {
      api.delete("/public/voice/preview/" + draftRef.current.draft_id, { params: { guest_id: guestId } }).catch(() => {});
      draftRef.current = null;
    }
    generation.current += 1;
    controller.current?.abort();
    if (recorder.current) {
      recorder.current.onstop = null;
      if (recorder.current.state !== "inactive") recorder.current.stop();
      recorder.current = null;
    }
    releaseMicrophone();
  };
  const cancel = () => {
    discard(); attemptedText.current = null; setEditing(false); setEnglishText(""); phaseRef.current = "idle"; setDraft(null); setPhase("idle"); setError(""); onBusyChange(false);
  };

  useEffect(() => {
    let alive = true;
    if (guestId) api.get("/public/voice/status", { params: { guest_id: guestId } })
      .then(({ data }) => { if (alive) setReady(Boolean(data.ready)); })
      .catch(() => { if (alive) setReady(false); });
    const hidden = () => {
      if (document.visibilityState === "hidden" && ["permission", "recording"].includes(phaseRef.current)) {
        cancel(); setError("Recording cancelled when the app went into the background. Please record again.");
      }
    };
    document.addEventListener("visibilitychange", hidden);
    return () => {
      alive = false; discard(); onBusyChange(false);
      document.removeEventListener("visibilitychange", hidden);
    };
    // The parent keys this component by caller; callbacks cannot change its session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestId]);

  const finish = () => {
    if (recorder.current?.state === "recording") {
      setPhase("processing");
      phaseRef.current = "processing";
      recorder.current.stop();
      releaseMicrophone();
    }
  };

  const record = async () => {
    if (!ready || !guestId || disabled || phaseRef.current !== "idle") return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setError("Recording is unavailable in this browser. Please open the app in Safari or Chrome."); return;
    }
    const session = ++generation.current;
    phaseRef.current = "permission";
    setError(""); setDraft(null); setSeconds(0); setPhase("permission"); onBusyChange(true);
    try {
      const microphone = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (session !== generation.current || (embedded && !holding.current)) { microphone.getTracks().forEach((track) => track.stop()); return; }
      stream.current = microphone;
      const mimeType = ["audio/mp4", "audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus"]
        .find((type) => MediaRecorder.isTypeSupported(type));
      const media = new MediaRecorder(microphone, { ...(mimeType ? { mimeType } : {}), audioBitsPerSecond: 64000 });
      recorder.current = media;
      const chunks = []; let size = 0;
      media.ondataavailable = (event) => {
        if (!event.data.size || session !== generation.current) return;
        size += event.data.size;
        if (size > 1024 * 1024) {
          cancel(); setError("Recording exceeded the test size limit. Please try a shorter message."); return;
        }
        chunks.push(event.data);
      };
      media.onerror = () => { if (session === generation.current) { cancel(); setError("Recording interrupted. Please try again."); } };
      media.onstop = async () => {
        if (session !== generation.current) return;
        releaseMicrophone(); setPhase("processing");
        controller.current = new AbortController();
        try {
          const audio = new Blob(chunks, { type: media.mimeType || mimeType || "audio/webm" });
          if (audio.size < 128) throw new Error("empty recording");
          const { data } = await api.post("/public/voice/preview", audio, {
            params: { guest_id: guestId }, headers: { "Content-Type": audio.type },
            timeout: 75000, signal: controller.current.signal,
          });
          if (session !== generation.current) return;
          attemptedText.current = null; setEditing(false); setEnglishText(data.english_text); setDraft(data); setPhase("review"); onBusyChange(false);
        } catch (err) {
          if (session !== generation.current) return;
          setPhase("idle"); onBusyChange(false);
          setError(formatErr(err.response?.data?.detail) || "Could not translate this recording. Please record again or use the call buttons.");
        } finally {
          chunks.length = 0;
          media.ondataavailable = null;
          media.onstop = null;
          if (recorder.current === media) recorder.current = null;
        }
      };
      media.start(1000); phaseRef.current = "recording"; setPhase("recording");
      const started = Date.now();
      timer.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - started) / 1000);
        setSeconds(Math.min(30, elapsed));
        if (elapsed >= 30) finish();
      }, 250);
    } catch (err) {
      if (session !== generation.current) return;
      discard(); setPhase("idle"); onBusyChange(false);
      setError("Microphone access was denied or unavailable. Check browser permissions, or use the call buttons.");
    }
  };

  useImperativeHandle(ref, () => ({
    startHold: () => { holding.current = true; record(); },
    endHold: () => {
      holding.current = false;
      if (phaseRef.current === "permission") { cancel(); setError("If prompted, allow microphone access, then hold the voice button again to speak."); }
      else finish();
    },
    cancelHold: () => { if (["permission", "recording"].includes(phaseRef.current)) cancel(); },
  }));

  const send = async () => {
    if (!draft || disabled || !englishText.trim() || phaseRef.current !== "review") return;
    if (onUseText) { if (onUseText(englishText.trim()) !== false) cancel(); return; }
    setPhase("sending"); phaseRef.current = "sending"; onBusyChange(true); setError("");
    try {
      // The draft is server-bound to this guest/charter and has one request ID.
      attemptedText.current = attemptedText.current ?? englishText.trim();
      const { data } = await api.post("/public/voice/send", { guest_id: guestId, draft_id: draft.draft_id, english_text: attemptedText.current });
      delivered.current = true; onBusyChange(false); onSent("Voice message", data);
    } catch (err) {
      setPhase("review"); onBusyChange(false);
      setError(formatErr(err.response?.data?.detail) || "Delivery could not be confirmed. Retrying this preview will not create a duplicate request.");
    }
  };

  const language = draft?.source_language || "und";
  let languageName = "Language not detected";
  try { if (language !== "und") languageName = new Intl.DisplayNames(["en"], { type: "language" }).of(language); } catch {}
  const flags = { el: "🇬🇷", en: "🇬🇧", fr: "🇫🇷", de: "🇩🇪", it: "🇮🇹", es: "🇪🇸", pt: "🇵🇹", ru: "🇷🇺", tr: "🇹🇷", ar: "🌐", zh: "🇨🇳", ja: "🇯🇵", ko: "🇰🇷", nl: "🇳🇱" };
  return <section className="service-voice-review space-y-4" data-testid="guest-voice-message">
    <div className="flex items-center justify-between gap-2"><h3 className="text-sm font-medium">{onUseText ? "Voice feedback comment" : "Voice message"}</h3><span className="call-alert-badge">Test version</span></div>
    <p className="text-xs text-muted-foreground">{onUseText ? "Speak in your language, then review the English comment. Audio is processed by OpenAI; only the reviewed text is used as feedback. This does not call the crew. Preview audio expires after 10 minutes." : "Speak in your language, then review the English message before sending. Audio is processed by OpenAI. Private crew playback is available for 24 hours after sending. Unsent previews expire after 10 minutes."}</p>
    {!guestId ? <p className="text-xs text-muted-foreground">Select your name above to record.</p> : !ready && <p className="text-xs text-muted-foreground">Voice translation is not enabled yet. The crew call buttons above still work.</p>}
    {phase === "idle" && !embedded && <Button variant="outline" className="w-full rounded-full" disabled={!ready || disabled || !guestId} onClick={record} data-testid="voice-record"><Mic className="h-4 w-4 mr-2" />Record message · up to 30 seconds</Button>}
    {phase === "permission" && <p role="status" className="text-sm">Allow microphone access in your browser…</p>}
    {phase === "recording" && !embedded && <div className="flex items-center justify-between gap-3"><span role="status" className="text-sm font-medium">● Recording · {seconds}s / 30s</span><Button onClick={finish} variant="outline" data-testid="voice-stop"><Square className="h-3 w-3 mr-2" />Stop</Button></div>}
    {["processing", "sending"].includes(phase) && <p role="status" className="text-sm flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />{phase === "processing" ? "Translating your message…" : "Sending to the crew…"}</p>}
    {draft && ["review", "sending"].includes(phase) && <div className="space-y-3" data-testid="voice-preview">
      <div className="rounded-2xl border border-border p-4"><div className="text-xs text-muted-foreground mb-2">Original message · <span title="Flag represents language, not nationality">{flags[language.split("-")[0]] || "🌐"} {languageName}</span></div><p className="text-sm whitespace-pre-wrap" dir="auto">{draft.source_text}</p></div>
      <div className="rounded-2xl bg-muted p-4"><label htmlFor="voice-english-text" className="block text-xs text-muted-foreground mb-2">English</label>{editing || !embedded ? <Textarea autoFocus={embedded} id="voice-english-text" value={englishText} onChange={(event) => setEnglishText(event.target.value)} maxLength={6000} rows={4} disabled={disabled || phase !== "review" || attemptedText.current !== null} data-testid="voice-english-text" /> : <p className="text-sm whitespace-pre-wrap" dir="auto">{englishText}</p>}</div>
      {attemptedText.current !== null && phase === "review" && <p className="text-xs text-muted-foreground">The attempted message is kept unchanged for a duplicate-safe retry.</p>}
      <div className="grid grid-cols-2 gap-3"><Button variant="outline" className="rounded-full" onClick={() => setEditing(!editing)} disabled={phase !== "review" || attemptedText.current !== null} data-testid="voice-edit"><Pencil className="h-4 w-4 mr-2" />{editing ? "Done editing" : "Edit"}</Button><Button className="rounded-full w-full" onClick={send} disabled={disabled || phase !== "review" || !englishText.trim()} data-testid="voice-send">{onUseText ? "Use this comment" : "Send to crew"}</Button></div>
    </div>}
    {phase !== "idle" && phase !== "sending" && <Button variant="ghost" size="sm" onClick={() => { cancel(); onBack?.(); }} data-testid="voice-cancel"><ArrowLeft className="h-4 w-4 mr-2" />{phase === "review" ? "Cancel / back" : "Cancel recording"}</Button>}
    {embedded && phase === "idle" && <Button variant="ghost" className="rounded-full" onClick={onBack}>Back to compass</Button>}
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
  </section>;
});
export default GuestVoiceMessage;
